// -------------------------------------------------------------------------
// Renders the selected admin table and provides filtering and sorting.
// -------------------------------------------------------------------------

import React, { useState } from 'react';
import AddRowForm from './AddRowForm';
import TableRow from './TableRow';


const DataTable = ({
    selectedTable,
    currentTableMeta,
    schema,
    rows,
    dropdownOptions,
    showAddForm,
    onToggleAddForm,
    newRowValues,
    onChangeNewRowValue,
    onAddRow,
    editingRowId,
    editingValues,
    onChangeEditingValue,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onDelete,
}) => {


    // -------------------------------------------------------------------------
    // Derived Table Information
    // -------------------------------------------------------------------------

    // Primary key used to uniquely identify rows in the selected table.
    const primaryKey = currentTableMeta?.primaryKey;


    // -------------------------------------------------------------------------
    // Filter & Sort State
    // -------------------------------------------------------------------------

    // Currently selected filter value for each column.
    const [columnFilters, setColumnFilters] = useState({});

    // Column whose action menu is currently open.
    const [activeColumnMenu, setActiveColumnMenu] = useState(null);

    // Currently selected action within the column menu.
    // Currently used for the filter options.
    const [activeColumnAction, setActiveColumnAction] = useState(null);

    // Column currently being sorted.
    const [sortColumn, setSortColumn] = useState(null);

    // Current sort direction for the selected column.
    const [sortDirection, setSortDirection] = useState(null);


    // -------------------------------------------------------------------------
    // Filtering
    // -------------------------------------------------------------------------

    // Update the selected filter for a column.
    const handleColumnFilterChange = (columnName, value) => {

        setColumnFilters(prev => ({
            ...prev,
            [columnName]: value
        }));

    };


    // Generate the available filter options for a column.
    //
    // Foreign-key columns use their existing dropdown options.
    // Boolean columns receive explicit True/False options.
    // Other columns use the unique values currently present in the table.
    const getFilterOptions = (column) => {

        // Foreign-key column.
        if (column.foreignKey) {

            const options =
                dropdownOptions?.[column.column_name] || [];

            return options.map(option => ({
                value: String(option.value),
                label: option.label
            }));
        }


        // Boolean column.
        if (
            column.data_type === "boolean" ||
            column.postgresType === "bool"
        ) {

            return [
                {
                    value: "true",
                    label: "True"
                },
                {
                    value: "false",
                    label: "False"
                }
            ];

        }


        // Normal column. (string and number values)
        const uniqueValues = [
            ...new Set(
                rows.map(row =>
                    row[column.column_name]
                )
            )
        ];


        return uniqueValues
            .filter(
                value =>
                    value !== null &&
                    value !== undefined
            )
            .map(value => ({
                value: String(value),
                label: String(value)
            }))
            .sort((a, b) =>
                a.label.localeCompare(b.label)
            );

    };


    // Apply all active column filters to the table rows.
    const filteredRows = rows.filter(row => {

        return schema.every(column => {

            const filter =
                columnFilters[column.column_name];

            // No filter selected for this column.
            if (
                filter === undefined ||
                filter === ""
            ) {
                return true;
            }

            return (
                String(row[column.column_name]) ===
                String(filter)
            );

        });

    });


    // -------------------------------------------------------------------------
    // Sorting
    // -------------------------------------------------------------------------

    // Sort a column. Selecting a new column starts ascending; clicking the
    // same column again reverses the sort direction.
    const handleColumnSort = (columnName) => {

        // Different column → ascending.
        if (sortColumn !== columnName) {

            setSortColumn(columnName);
            setSortDirection("asc");

            return;
        }

        // Same column → flip direction.
        setSortDirection(
            sortDirection === "asc"
                ? "desc"
                : "asc"
        );

    };


    // Apply the selected sort to the filtered rows.
    const sortedRows = [...filteredRows].sort((a, b) => {

        if (!sortColumn || !sortDirection) {
            return 0;
        }

        const aValue = a[sortColumn];
        const bValue = b[sortColumn];

        // NULL values are placed at the bottom.
        if (
            aValue === null ||
            aValue === undefined
        ) {
            return 1;
        }

        if (
            bValue === null ||
            bValue === undefined
        ) {
            return -1;
        }


        // Numeric sorting.
        if (
            typeof aValue === "number" &&
            typeof bValue === "number"
        ) {

            return sortDirection === "asc"
                ? aValue - bValue
                : bValue - aValue;

        }


        // Boolean sorting.
        if (
            typeof aValue === "boolean" &&
            typeof bValue === "boolean"
        ) {
            const comparison =
                Number(aValue) - Number(bValue);

            return sortDirection === "asc"
                ? comparison
                : -comparison;
        }


        // Text sorting.
        const comparison =
            String(aValue).localeCompare(
                String(bValue),
                undefined,
                {
                    numeric: true,
                    sensitivity: "base"
                }
            );

        return sortDirection === "asc"
            ? comparison
            : -comparison;
    });


    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    return (

        <section className="adtab-panel">


            {/* -----------------------------------------------------------------
                Table Header
            ----------------------------------------------------------------- */}

            <header className="adtab-panel__header">

                <div>

                    <span className="adtab-panel__eyebrow">
                        Table
                    </span>

                    <h2 className="adtab-panel__title">
                        {selectedTable}
                    </h2>

                </div>


                <div className="adtab-panel__meta">

                    <span className="adtab-row-count">
                        {rows.length} row
                        {rows.length === 1 ? '' : 's'}
                    </span>


                    {currentTableMeta?.editable && (

                        <button
                            className="adtab-btn adtab-btn--primary"
                            onClick={onToggleAddForm}
                        >
                            {showAddForm ? 'Cancel' : 'Add row'}
                        </button>

                    )}

                </div>

            </header>


            {/* -----------------------------------------------------------------
                Add Row Form
            ----------------------------------------------------------------- */}

            {showAddForm && (

                <AddRowForm
                    schema={schema}
                    primaryKey={primaryKey}
                    values={newRowValues}
                    dropdownOptions={dropdownOptions}
                    onChange={onChangeNewRowValue}
                    onSave={onAddRow}
                />

            )}


            {/* -----------------------------------------------------------------
                Table
            ----------------------------------------------------------------- */}

            <div className="adtab-table-scroll">

                <table className="adtab-table">


                    {/* ---------------------------------------------------------
                        Table Header / Column Menus
                    --------------------------------------------------------- */}

                    <thead>

                        <tr>

                            {schema.map(column => (

                                <th key={column.column_name}>

                                    <button
                                        className="adtab-column-header"
                                        onClick={() =>
                                            setActiveColumnMenu(
                                                activeColumnMenu === column.column_name
                                                    ? null
                                                    : column.column_name
                                            )
                                        }
                                    >

                                        <span className="adtab-th__name">

                                            {column.column_name}

                                            {sortColumn === column.column_name && (

                                                <span className="adtab-sort-indicator">
                                                    {sortDirection === "asc"
                                                        ? " ↑"
                                                        : " ↓"
                                                    }
                                                </span>

                                            )}

                                        </span>


                                        <span className="adtab-th__type">
                                            {column.data_type}
                                        </span>


                                        {columnFilters[column.column_name] && (

                                            <span className="adtab-filter-indicator">
                                                ●
                                            </span>

                                        )}

                                    </button>


                                    {/* -------------------------------------------------
                                        Column Action Menu
                                    ------------------------------------------------- */}

                                    {activeColumnMenu === column.column_name && (

                                        <div className="adtab-column-menu">


                                            {/* Sort */}

                                            <button
                                                type="button"
                                                className="adtab-column-menu__action"
                                                onClick={() => {

                                                    handleColumnSort(
                                                        column.column_name
                                                    );

                                                    setActiveColumnMenu(null);

                                                }}
                                            >

                                                <span>
                                                    Sort
                                                </span>


                                                {sortColumn === column.column_name && (

                                                    <span className="adtab-sort-menu-indicator">
                                                        {sortDirection === "asc"
                                                            ? "↑"
                                                            : "↓"
                                                        }
                                                    </span>

                                                )}

                                            </button>


                                            {/* Filter */}

                                            <button
                                                type="button"
                                                className="adtab-column-menu__action"
                                                onClick={() =>
                                                    setActiveColumnAction(
                                                        activeColumnAction === "filter"
                                                            ? null
                                                            : "filter"
                                                    )
                                                }
                                            >
                                                Filter
                                            </button>


                                            {/* Filter Options */}

                                            {activeColumnAction === "filter" && (

                                                <div className="adtab-filter-options">


                                                    {/* All */}

                                                    <button
                                                        type="button"
                                                        className={
                                                            `adtab-filter-option ${
                                                                !columnFilters[column.column_name]
                                                                    ? "adtab-filter-option--active"
                                                                    : ""
                                                            }`
                                                        }
                                                        onClick={() => {

                                                            handleColumnFilterChange(
                                                                column.column_name,
                                                                ""
                                                            );

                                                            setActiveColumnMenu(null);
                                                            setActiveColumnAction(null);

                                                        }}
                                                    >
                                                        All
                                                    </button>


                                                    {/* Available Values */}

                                                    {getFilterOptions(column).map(option => {

                                                        const isActive =
                                                            String(
                                                                columnFilters[column.column_name]
                                                            ) === String(option.value);


                                                        return (

                                                            <button
                                                                type="button"
                                                                key={option.value}
                                                                className={
                                                                    `adtab-filter-option ${
                                                                        isActive
                                                                            ? "adtab-filter-option--active"
                                                                            : ""
                                                                    }`
                                                                }
                                                                onClick={() => {

                                                                    handleColumnFilterChange(
                                                                        column.column_name,
                                                                        String(option.value)
                                                                    );

                                                                    setActiveColumnMenu(null);
                                                                    setActiveColumnAction(null);

                                                                }}
                                                            >
                                                                {option.label}
                                                            </button>

                                                        );

                                                    })}

                                                </div>

                                            )}

                                        </div>

                                    )}

                                </th>

                            ))}


                            <th className="adtab-th--actions">
                                Actions
                            </th>

                        </tr>

                    </thead>


                    {/* ---------------------------------------------------------
                        Table Rows
                    --------------------------------------------------------- */}

                    <tbody>

                        {sortedRows.map((row, index) => {

                            const rowId =
                                row[primaryKey];

                            const isEditing =
                                editingRowId === rowId;


                            return (

                                <TableRow
                                    key={index}
                                    row={row}
                                    schema={schema}
                                    primaryKey={primaryKey}
                                    dropdownOptions={dropdownOptions}
                                    isEditing={isEditing}
                                    editingValues={editingValues}
                                    onChangeEditingValue={
                                        onChangeEditingValue
                                    }
                                    editable={
                                        currentTableMeta?.editable
                                    }
                                    allowDelete={
                                        currentTableMeta?.allowDelete
                                    }
                                    onStartEdit={onStartEdit}
                                    onSaveEdit={onSaveEdit}
                                    onCancelEdit={onCancelEdit}
                                    onDelete={onDelete}
                                />

                            );

                        })}

                    </tbody>

                </table>


                {/* -----------------------------------------------------------------
                    Empty States
                ----------------------------------------------------------------- */}

                {rows.length === 0 && (

                    <p className="adtab-empty">
                        This table has no rows yet.
                    </p>

                )}


                {rows.length > 0 &&
                    filteredRows.length === 0 && (

                    <p className="adtab-empty">
                        No rows match the current filters.
                    </p>

                )}

            </div>

        </section>

    );

};


export default DataTable;